import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  offset as offsetMiddleware,
  flip,
  shift,
  autoUpdate,
  useFloating,
  useInteractions,
  useRole,
  useDismiss,
  useClick,
  useTransitionStyles,
} from "@floating-ui/react";

import { noop } from "~/utils/helpers";

import { PopoverTrigger } from "./PopoverTrigger";
import { PopoverPanel } from "./PopoverPanel";

import "./Popover.css";

import type { ReactElement } from "react";
import type {
  FloatingContext,
  OffsetOptions,
  Placement,
  Strategy,
} from "@floating-ui/react";
import type { PopoverTriggerProps } from "./PopoverTrigger";

type PopoverChildren = ReactElement<
  typeof PopoverTrigger | typeof PopoverPanel
>;

export interface PopoverRenderPropArg {
  close: () => void;
}

export interface PopoverProps {
  children:
    | PopoverChildren
    | PopoverChildren[]
    | ((props: PopoverRenderPropArg) => PopoverChildren[]);
  elevation?: 0 | 1 | 2 | 3;
  fullWidth?: boolean;
  offset?: OffsetOptions;
  placement?: (typeof POPOVER_PLACEMENTS)[number];
  strategy?: Strategy;
  onOpen?(): void;
  onClose?(): void;
}

export interface PopoverContextValue {
  isOpen: boolean;
  isMounted: boolean;
  floatingContext?: FloatingContext;
  close: () => void;
  getPanelProps: () => Record<string, unknown>;
  getFloatingProps: () => Record<string, unknown>;
  getTriggerProps: (props: PopoverTriggerProps) => PopoverTriggerProps;
}

export const POPOVER_PLACEMENTS: Placement[] = [
  "top",
  "top-start",
  "top-end",
  "right",
  "right-start",
  "right-end",
  "bottom",
  "bottom-start",
  "bottom-end",
  "left",
  "left-start",
  "left-end",
];

export const POPOVER_ANIMATION_DURATION = 250;

export const PopoverContext = createContext<PopoverContextValue>({
  isOpen: false,
  isMounted: false,
  close: noop,
  getFloatingProps: () => ({}),
  getPanelProps: () => ({}),
  getTriggerProps: (props) => props,
});

export function usePopover() {
  return useContext(PopoverContext);
}

export function Popover({
  children,
  elevation = 3,
  fullWidth,
  placement = "bottom",
  offset,
  strategy = "absolute",
  onClose,
  onOpen,
}: PopoverProps) {
  const [isOpen, setIsOpen] = useState(false);

  const {
    floatingStyles,
    context: floatingContext,
    refs,
  } = useFloating({
    open: isOpen,
    middleware: [offsetMiddleware(offset), flip(), shift()],
    placement,
    strategy,
    onOpenChange(open) {
      setIsOpen(open);

      if (open) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    whileElementsMounted: autoUpdate,
  });

  const { isMounted, styles: transitionStyles } = useTransitionStyles(
    floatingContext,
    {
      duration: {
        open: POPOVER_ANIMATION_DURATION,
        close: POPOVER_ANIMATION_DURATION * 0.5,
      },
      initial: {
        opacity: 0,
        transform: "translateY(var(--size-1))",
      },
      open: {
        opacity: 1,
        transform: "translateY(0)",
      },
      close: {
        opacity: 0,
      },
    },
  );

  const interactions = useInteractions([
    useClick(floatingContext),
    useRole(floatingContext),
    useDismiss(floatingContext),
  ]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Props for the floating element
  const getFloatingProps = useCallback(() => {
    return {
      ...interactions.getFloatingProps({
        style: { ...floatingStyles, zIndex: "var(--z-90)" },
      }),
      "data-full-width": fullWidth,
      ref: refs.setFloating,
    };
  }, [floatingStyles, fullWidth, interactions, refs.setFloating]);

  // Props for the panel within the floating element
  const getPanelProps = useCallback(() => {
    return {
      "data-elevation": elevation,
      style: transitionStyles,
    };
  }, [elevation, transitionStyles]);

  // Props for the trigger element
  const getTriggerProps = useCallback(
    (props: PopoverTriggerProps) => {
      return {
        ...props,
        ...interactions.getReferenceProps(),
        ref: refs.setReference,
      };
    },
    [interactions, refs.setReference],
  );

  const ctx = useMemo<PopoverContextValue>(
    () => ({
      floatingContext,
      isMounted,
      isOpen,
      close,
      getFloatingProps,
      getPanelProps,
      getTriggerProps,
    }),
    [
      close,
      floatingContext,
      getFloatingProps,
      getPanelProps,
      getTriggerProps,
      isMounted,
      isOpen,
    ],
  );

  const render = useMemo(() => {
    if (typeof children === "function") {
      return children({ close });
    }

    return children;
  }, [children, close]);

  return (
    <PopoverContext.Provider value={ctx}>{render}</PopoverContext.Provider>
  );
}

Popover.Trigger = PopoverTrigger;
Popover.Panel = PopoverPanel;
